// Gerbang dini checkout multi-produk — helper murni (lihat
// src/lib/__tests__/checkoutGuard.test.ts). Data-layer:
// `assertCartsOrderable` di src/lib/supabase/data.ts.
//
// KENAPA ADA (bughunt ketiga, 2026-09-30): pembeli zzhomey checkout 3 produk
// (Cheryl, Aluna, Dera). "Aluna Brooch" sudah DINONAKTIFKAN OTOMATIS karena
// stoknya habis. RLS produk tetap membukanya bagi pembeli yang pernah
// memesannya ("Customers read ordered products"), jadi cek status per produk
// lolos — tapi RLS varian menuntut produk aktif, sehingga insert `order_items`
// gagal ("Variation must belong to the order product"). Checkout berjalan
// per produk, jadi Cheryl & Dera SUDAH jadi pesanan saat Aluna gagal, dan
// pesan galatnya menyuruh "tambahkan lagi ke keranjang". Pembeli menambahkan
// ulang semuanya → Cheryl & Dera terpesan DUA KALI (CO-2609300194/-195).
//
// Aturannya: SEMUA produk keranjang dicek SEKALI, SEBELUM satu pesanan pun
// dibuat. Produk yang tak terbaca, nonaktif, atau statusnya tak dibuka untuk
// pemesanan menghentikan seluruh checkout dengan daftar yang harus dihapus —
// tak ada lagi sukses sebagian karena sebab ini.

import { isOrderableStatus } from "@/lib/products";

/** Kolom `products` yang dibaca gerbang (`id, name, status, product_type, is_active`). */
export type CheckoutProductRow = {
  id: string;
  name?: string | null;
  status?: string | null;
  product_type?: string | null;
  is_active?: boolean | null;
};

export type UnorderableReason = "tidak_dijual" | "tidak_dibuka";

export type UnorderableCart = {
  productId: string;
  productName: string;
  reason: UnorderableReason;
};

/**
 * Keranjang yang produknya TAK boleh dipesan saat ini, urut keranjang, tiap
 * produk sekali.
 *   • baris tak terbaca (RLS menyembunyikan produk nonaktif yang belum pernah
 *     dipesan pembeli ini) atau `is_active = false` → "tidak_dijual";
 *   • status tak dibuka untuk pemesanan (cermin `isOrderableStatus`, yang juga
 *     dicerminkan trigger `enforce_orderable_product_on_order`) → "tidak_dibuka".
 */
export function findUnorderableCarts(
  carts: readonly { productId: string; productName: string }[],
  rows: readonly CheckoutProductRow[]
): UnorderableCart[] {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const seen = new Set<string>();
  const out: UnorderableCart[] = [];
  for (const cart of carts) {
    if (seen.has(cart.productId)) continue;
    seen.add(cart.productId);
    const row = byId.get(cart.productId);
    const productName = row?.name?.trim() || cart.productName?.trim() || "Produk";
    if (!row || row.is_active === false) {
      out.push({ productId: cart.productId, productName, reason: "tidak_dijual" });
    } else if (!isOrderableStatus(row.status, row.product_type)) {
      out.push({ productId: cart.productId, productName, reason: "tidak_dibuka" });
    }
  }
  return out;
}

/** Kalimat untuk pembeli, atau null bila semua produk boleh dipesan. */
export function unorderableCartsMessage(list: readonly UnorderableCart[]): string | null {
  if (list.length === 0) return null;
  const parts = list.map(
    (u) =>
      `${u.productName} (${
        u.reason === "tidak_dijual"
          ? "sudah tidak dijual — stok habis atau dinonaktifkan toko"
          : "sedang tidak dibuka untuk pemesanan"
      })`
  );
  return (
    `Belum ada pesanan yang dibuat. Produk berikut sudah tidak bisa dipesan: ` +
    `${parts.join("; ")}. Hapus dari keranjang, lalu checkout lagi.`
  );
}

/**
 * Kalimat penolakan baris keranjang yang variannya sudah tak terbaca pembeli —
 * SATU kalimat untuk dua cabang resolver checkout (`buildCheckoutLineResolver`
 * di data.ts): varian tak ditemukan di daftar yang terbaca, dan daftar varian
 * produknya terbaca KOSONG sementara baris keranjang membawa id varian nyata.
 *
 * Cabang kedua lahir dari bughunt ketiga (2026-09-30): produk yang dinonaktifkan
 * otomatis (stok habis) memulangkan NOL varian ke pembeli (RLS varian menuntut
 * produk & varian aktif), dan resolver dulu jatuh ke jalur cadangan yang
 * memakai id varian DAN harga dari keranjang localStorage — pesanannya dibuat,
 * lalu insert item gagal "Variation must belong to the order product".
 * Sengaja TIDAK menyuruh "muat ulang lalu tambahkan lagi": varian yang dimatikan
 * tak akan pernah muncul lagi dengan memuat ulang.
 */
export function unavailableVariantMessage(variationName: string): string {
  return (
    `Variasi "${variationName}" sudah tidak dijual. ` +
    "Hapus baris ini dari keranjang, lalu pilih variasi lain di halaman produk."
  );
}

/**
 * Pesan hasil checkout multi-produk yang sebagian gagal. Kode pesanan yang
 * SUDAH jadi disebut dan pembeli diberi tahu untuk TIDAK memesannya ulang —
 * kalimat lama ("N produk masuk") membuat pembeli menambahkan ulang semuanya.
 */
export function partialCheckoutMessage(
  succeeded: readonly { productName: string; orderCode: string }[],
  failed: readonly { productName: string; error: string }[]
): string {
  const gagal = failed.map((f) => `${f.productName} (${f.error})`).join("; ");
  if (succeeded.length === 0) return `Gagal membuat pesanan: ${gagal}`;
  const masuk = succeeded.map((s) => `${s.productName} (${s.orderCode})`).join(", ");
  return (
    `Pesanan sudah masuk untuk ${masuk} — tidak perlu dipesan ulang. ` +
    `Yang gagal: ${gagal}`
  );
}
